import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, Link } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import { Formik, Field } from 'formik'
import { DEFAULT_COUNTRY_CODE } from 'lib/core/resources/constants'
import { subscribe } from 'lib/core/data/customer/subscribe'
import { getSlice } from 'lib/core/data/customer/subscribe/selectors'
import { useDirectoryOptions, useCreateAccount, useLogin } from 'lib/core/hooks'
import { Button, Form, Grid, Alert } from 'lib/ui/components'
import { PublicLayout, Title } from '../../../../components/Styled'
import { schema, validation, FormValues } from './schema'
import { InterceptBlurEvent } from 'lib/ui/components/Form/types'
import { captureEmail } from 'modules/listrak'
import { useShippingRegionOptions } from 'hooks/useShippingRegionOptions'

interface LoginData {
  email: string
  password: string
  persistent: boolean
}

interface Prop {
  avoidRedirect?: boolean
}

export const CreateAccount = ({ avoidRedirect }: Prop) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const history = useHistory()
  const [subscribeCustomer, setSubscribeCustomer] = useState(false)
  const [createAccountSuccess, setCreateAccountSuccess] = useState(false)
  const [loginData, setLoginData] = useState<LoginData | undefined>()
  const { isSuccess: isSubscribeSuccess } = useSelector(getSlice)
  const { createAccount, isFetching: createAccountIsFetching, errorCode } = useCreateAccount()
  const { login, isFetching: loginIsFetching } = useLogin({
    onSuccess: () => {
      if (avoidRedirect) {
        return
      }

      history.replace('/customer/account')
    }
  })

  const isFetching = createAccountIsFetching || loginIsFetching

  const {
    countryInfoIsFetching
  } = useDirectoryOptions({ countryCode: DEFAULT_COUNTRY_CODE })
  const { shippingRegionOptions: regionOptions } = useShippingRegionOptions({ countryCode: DEFAULT_COUNTRY_CODE })

  const handleSubmit = (values: FormValues) => {
    const selectedRegionInfo = regionOptions.find((region) => region.value === values.state)
    const regionId = selectedRegionInfo?.id

    const address = {
      city: values.city,
      country_id: DEFAULT_COUNTRY_CODE,
      default_shipping: true,
      default_billing: true,
      firstname: values.firstName,
      lastname: values.lastName,
      postcode: values.zipCode,
      region_id: regionId,
      street: [values.street],
      telephone: values.telephone
    }

    if (subscribeCustomer) {
      dispatch(subscribe(values.email))
    }

    createAccount({
      email: values.email,
      firstName: values.firstName,
      lastName: values.lastName,
      password: values.password,
      address
    }, {
      callback: {
        onSuccess: () => {
          setLoginData({
            email: values.email,
            password: values.password,
            persistent: Boolean(process.env.REACT_APP_PERSISTENT_LOGIN)
          })
          setCreateAccountSuccess(true)
        }
      }
    })
  }

  useEffect(() => {
    if (createAccountSuccess) {
      if (subscribeCustomer) {
        if (isSubscribeSuccess) login(loginData as LoginData)
      } else {
        login(loginData as LoginData)
      }
    }
    // eslint-disable-next-line
  }, [createAccountSuccess, subscribeCustomer, isSubscribeSuccess])

  return (
    <PublicLayout className='create-account'>
      <div>
        <Title>
          <FormattedMessage
            id='customer.account.create.title'
          />
        </Title>

        <Formik
          initialValues={schema}
          validationSchema={validation(intl)}
          onSubmit={handleSubmit}
        >
          <Form.Wrapper>
            <Grid>
              <div>
                <Field
                  name='firstName'
                  label={intl.formatMessage({
                    id: 'customer.account.create.label.firstName'
                  })}
                  component={Form.Input}
                  isRequired
                  autoFocus
                />
              </div>
              <div>
                <Field
                  name='lastName'
                  label={intl.formatMessage({
                    id: 'customer.account.create.label.lastName'
                  })}
                  component={Form.Input}
                  isRequired
                />
              </div>
            </Grid>

            <Field
              name='email'
              label={intl.formatMessage({
                id: 'customer.account.create.label.email'
              })}
              component={Form.Input}
              interceptBlur={(e: InterceptBlurEvent<string>) => {
                captureEmail(e.value)
              }}
              isRequired
            />

            <Field
              name='password'
              label={intl.formatMessage({
                id: 'customer.account.create.label.password'
              })}
              component={Form.Input}
              type='password'
              isRequired
            />

            <Field
              name='passwordRetype'
              label={intl.formatMessage({
                id: 'customer.account.create.label.passwordRetype'
              })}
              component={Form.Input}
              type='password'
              isRequired
            />
            <h4>Address Information</h4>
            <Field
              name='telephone'
              label={intl.formatMessage({
                id: 'customer.account.create.label.phone'
              })}
              component={Form.Input}
              isRequired
            />
            <Field
              name='street'
              label={intl.formatMessage({
                id: 'customer.account.create.label.street'
              })}
              component={Form.Input}
              isRequired
            />
            <Field
              name='city'
              label={intl.formatMessage({
                id: 'customer.account.create.label.city'
              })}
              component={Form.Input}
              isRequired
            />
            <Field
              name='state'
              label={intl.formatMessage({
                id: 'customer.account.create.label.state'
              })}
              component={Form.Select}
              options={regionOptions}
              isLoading={countryInfoIsFetching}
              isRequired
            />
            <Field
              name='zipCode'
              label={intl.formatMessage({
                id: 'customer.account.create.label.zipcode'
              })}
              component={Form.Input}
              isRequired
            />
            <div className='bottom__checkbox'>
              <label>
                <input
                  type='checkbox'
                  checked={subscribeCustomer}
                  onChange={e => setSubscribeCustomer(e.target.checked)}
                />
                <span>Sign Up for Newsletter</span>
              </label>
            </div>
            <Form.Actions.Wrapper>
              <>
                {!isFetching && errorCode && (
                  <Alert type='danger'>
                    <FormattedMessage
                      id={errorCode}
                      values={{
                        loginLink: (str: string) => <Link to='/login'>{str}</Link>,
                        resetPasswordLink: (str: string) => <Link to='/customer/account/forgotpassword'>{str}</Link>
                      }}
                    />
                  </Alert>
                )}
                <Button
                  type='submit'
                  isLoading={isFetching}
                >
                  <FormattedMessage id='action.createAccount' />
                </Button>
              </>
            </Form.Actions.Wrapper>
          </Form.Wrapper>
        </Formik>
      </div>
    </PublicLayout>
  )
}
